import React from 'react';
import { motion } from 'framer-motion';
import { 
  CpuChipIcon, 
  CloudIcon, 
  CodeBracketIcon, 
  CubeIcon,
  ArrowRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useInView } from 'react-intersection-observer';

const Services = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const services = [
    {
      icon: CpuChipIcon,
      title: 'IoT Hardware-Software Integration',
      description: 'End-to-end IoT solutions from sensor integration to real-time monitoring dashboards.',
      features: [
        'Arduino, Raspberry Pi, ESP32 Programming',
        'Industrial IoT & Sensor Systems',
        'MQTT, LoRaWAN Connectivity',
        'Real-time Data Processing',
        'Custom Firmware Development'
      ],
      gradient: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20'
    },
    {
      icon: CloudIcon,
      title: 'AWS Cloud Architecture & Deployment',
      description: 'Scalable, secure cloud infrastructure with auto-scaling and high availability.',
      features: [
        'EC2 Auto-scaling & Load Balancing',
        'Lambda Serverless Computing',
        'RDS Database Optimization',
        'CI/CD Pipeline Setup',
        'CloudWatch Monitoring'
      ],
      gradient: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20'
    },
    {
      icon: CodeBracketIcon,
      title: 'Python Development (Django/FastAPI)',
      description: 'High-performance web applications and APIs with modern Python frameworks.',
      features: [
        'Django Full-stack Applications',
        'FastAPI High-performance APIs',
        'Database Design & Optimization',
        'RESTful API Development',
        'Authentication & Security'
      ],
      gradient: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20'
    },
    {
      icon: CubeIcon,
      title: 'System Architecture & Design',
      description: 'Comprehensive system design for scalable, maintainable enterprise solutions.',
      features: [
        'Microservices Architecture',
        'Database Schema Design',
        'API Architecture Planning',
        'Performance Optimization',
        'Security Implementation'
      ],
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="services" className="section-padding bg-gray-50 dark:bg-gray-800">
      <div className="max-w-7xl mx-auto container-padding">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Professional <span className="gradient-text">Services</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Comprehensive technical solutions tailored to your business needs. 
              From IoT integration to cloud deployment, I deliver results that matter.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={cardVariants}
                className="relative group"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                <div className={`relative card p-8 h-full bg-gradient-to-br ${service.bgGradient} border border-white/20`}>
                  {/* Service Icon */}
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${service.gradient} mb-6`}>
                    <service.icon className="h-8 w-8 text-white" />
                  </div>

                  {/* Service Title */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {service.title}
                  </h3>

                  {/* Service Description */}
                  <p className="text-gray-600 dark:text-gray-300 mb-6">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <div className="space-y-3 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="flex justify-center pt-6 border-t border-gray-200 dark:border-gray-700">
                    <motion.a
                      href="https://fiverr.com/dhanishvijayan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      View on Fiverr
                      <ArrowRightIcon className="h-5 w-5 ml-2" />
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Service Highlights */}
          <motion.div
            variants={itemVariants}
            className="grid md:grid-cols-3 gap-8 mb-12"
          >
            {[
              {
                title: 'Fast Delivery',
                description: '48-hour emergency delivery available',
                icon: '⚡',
              },
              {
                title: 'Quality Guarantee',
                description: '99.9% uptime and performance guarantee',
                icon: '🛡️',
              },
              {
                title: '24/7 Support',
                description: 'Ongoing support and maintenance included',
                icon: '🚀',
              },
            ].map((highlight, index) => (
              <motion.div
                key={index}
                className="text-center p-6 bg-white dark:bg-gray-900 rounded-xl shadow-sm"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              >
                <div className="text-4xl mb-4">{highlight.icon}</div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {highlight.title}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {highlight.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Process Section */}
          <motion.div variants={itemVariants} className="text-center">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
              My Development Process
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: '01', title: 'Discovery', desc: 'Understanding your requirements and goals' },
                { step: '02', title: 'Planning', desc: 'Creating detailed technical specifications' },
                { step: '03', title: 'Development', desc: 'Building and testing your solution' },
                { step: '04', title: 'Deployment', desc: 'Going live with ongoing support' },
              ].map((process, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 + index * 0.1 }}
                >
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-sm">
                    <div className="text-3xl font-bold text-primary-600 dark:text-primary-400 mb-3">
                      {process.step}
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {process.title}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {process.desc}
                    </p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-primary-600 dark:bg-primary-400"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;